import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { RouterLink } from '@angular/router';

/**
 * A space's name, anywhere it appears, as the way into that space.
 *
 * The events board already linked its titles; everywhere else an event was a
 * title with no way through - the claim funnel's rows, the spaces on an abuse
 * case, the chips in a profile, a lobby chat in the engagement table. All of
 * them hold an event id, so all of them can go to the same page.
 *
 * A route, not a dialog, because the event page is a page: members, lobby
 * history, organizer, the lot. The dialog treatment belongs to people, whose
 * detail fits in one.
 */
@Component({
  selector: 'app-event-link',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <a
      *ngIf="eventId; else plain"
      class="event-link"
      [routerLink]="['/admin/spaces', eventId]"
      [title]="'Open ' + label + ' — members, lobby and organizer'"
      (click)="$event.stopPropagation()"
      >{{ label }}</a
    >
    <ng-template #plain><span class="event-link plain">{{ label }}</span></ng-template>
  `,
  styles: [
    `
      :host {
        display: inline;
      }

      .event-link {
        color: inherit;
        text-decoration: none;
        font-weight: 600;
      }

      a.event-link:hover {
        color: #1f6f66;
        text-decoration: underline;
      }

      a.event-link:focus-visible {
        outline: 2px solid #1f6f66;
        outline-offset: 2px;
        border-radius: 3px;
      }

      .event-link.plain {
        font-weight: inherit;
      }
    `,
  ],
})
export class EventLinkComponent {
  /** The space to open. Without one the title is shown, but not as a link. */
  @Input() eventId: string | null = null;
  /** What to call it. Falls back to the id, which is at least searchable. */
  @Input() title: string | null | undefined = null;
  /** Shown when there is neither a title nor an id. */
  @Input() fallback = '—';

  get label(): string {
    return this.title?.trim() || this.eventId || this.fallback;
  }
}
